"use client";

import { useState } from "react";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { zodResolver } from "@hookform/resolvers/zod";
import { DEFAULT_COUNTRY_CODE, ROUTES } from "@unreserved/constants";
import { useSignup } from "@unreserved/hooks/use-auth";
import { useToast } from "@unreserved/hooks/use-toast";
import { type SignupFormData, passwordRules, signupSchema } from "@unreserved/schemas/auth-schemas";
import type { PropertyAddressSuggestionsDto } from "@unreserved/sdk";
import { userApi } from "@unreserved/services/sdk-client";
import { Controller, useForm } from "react-hook-form";

import { DefaultAvatarIcon, UploadIcon } from "@/app/_components/icons";
import { AppButton, FormInput, PhoneInput } from "@/app/_components/shared";
import { AddressSearchInput } from "@/app/_components/shared/search-input";
import { IMAGES } from "@/app/_libs/constants/images";
import { registerFcmToken } from "@/app/_libs/notifications";
import { useAnalytics } from "@/hooks/use-analytics";
import { useImageUpload } from "@/hooks/use-image-upload";
import { useUploadProfileImage } from "@/hooks/use-profile";
import { handleHttpError } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";

import { SocialLogin } from "../login/social-login";
import { PasswordValidationRules } from "../reset-password/password-validation-rules";

const INTEREST_OPTIONS = ["Selling", "Buying", "Market Updates"] as const;
type Interest = (typeof INTEREST_OPTIONS)[number];

export const SignupForm = () => {
  const [countryCode, setCountryCode] = useState<string>(DEFAULT_COUNTRY_CODE);
  const [selectedProperty, setSelectedProperty] = useState<PropertyAddressSuggestionsDto | null>(
    null,
  );
  const [interests, setInterests] = useState<Interest[]>([]);
  const { signupMutation } = useSignup();
  const { trackSignup } = useAnalytics();
  const uploadProfileImage = useUploadProfileImage();
  const { toast } = useToast();
  const setUser = useAuthStore((state) => state.setUser);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, touchedFields },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      countryCode: DEFAULT_COUNTRY_CODE,
      contactNumber: "",
      profileImage: "",
      propertyAddress: "",
    },
  });
  const router = useRouter();
  const password = watch("password");

  const {
    preview: profileImagePreview,
    handleImageUpload,
    file: profileImageFile,
  } = useImageUpload({
    onSuccess: (dataUrl) => {
      setValue("profileImage", dataUrl);
    },
    onError: (error) => {
      toast.error("Image upload failed", {
        description: error.message,
      });
    },
  });

  const onSubmit = async (data: SignupFormData) => {
    const fullName = data.lastName?.trim()
      ? `${data.firstName} ${data.lastName}`.trim()
      : data.firstName;

    try {
      const response = await signupMutation.mutateAsync({
        fullName,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        countryCode: data.countryCode,
        phoneNumber: data.contactNumber,
        propertyAddress: data.propertyAddress,
        interests,
      });

      if (response.status !== "Success") return;

      userApi.userControllerGetProfileV1().then((profile) => {
        const userProfile = profile.data;

        if (!userProfile) {
          return;
        }

        setUser(userProfile);
        trackSignup("email", userProfile.id);
      });

      if (profileImageFile) {
        await uploadProfileImage.mutateAsync(profileImageFile);
      }
      registerFcmToken();

      toast.success("Signup successful", {
        description: response.message ?? "Your account has been created successfully",
      });

      router.replace(ROUTES.DASHBOARD.HOME);
    } catch (error) {
      handleHttpError(error as Error & { response?: Response }, "Signup failed", toast);
    }
  };

  return (
    <div className="bg-background-0 w-full p-4 lg:max-w-[40vw] lg:rounded-lg lg:p-10.5 lg:shadow-[0px_4px_16px_0px_rgba(38,38,38,0.12)]">
      <div className="mb-10.5">
        <div className="mb-2 lg:mb-2">
          <Image
            src={IMAGES.LOGO}
            alt="Unreserved Logo"
            width={130}
            height={32}
            className="lg:h-8 lg:w-44.75"
            objectFit="contain"
          />
        </div>
        <h1 className="text-tertiary-950 mb-1 text-xl font-semibold lg:mb-1 lg:text-4xl">
          Welcome!
        </h1>
        <p className="text-typography-600 text-xs leading-normal lg:text-sm">
          Enter details to continue
        </p>
      </div>

      <form id="signup-form" onSubmit={handleSubmit(onSubmit)} className="mb-6 space-y-6">
        <div className="flex items-center gap-4">
          <div className="bg-primary-100 flex h-17 w-17 items-center justify-center overflow-hidden rounded-full">
            {profileImagePreview ? (
              <Image
                src={profileImagePreview}
                alt="Profile preview"
                width={68}
                height={68}
                className="h-full w-full object-cover"
              />
            ) : (
              <DefaultAvatarIcon className="h-8 w-8" />
            )}
          </div>
          <label className="cursor-pointer">
            <div className="border-typography-300 hover:border-primary-500 flex h-9 items-center gap-2 rounded border bg-white px-4 py-2 transition-colors">
              <UploadIcon className="h-4 w-4" />
              <span className="text-typography-800 text-sm font-medium">
                Upload Photo (Optional)
              </span>
            </div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageUpload}
              className="hidden"
              disabled={isSubmitting}
              aria-label="Upload photo"
            />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-4">
          <FormInput
            label="First name"
            type="text"
            placeholder="Enter first name"
            error={errors.firstName?.message}
            disabled={isSubmitting}
            {...register("firstName")}
          />

          <FormInput
            label="Last name (Optional)"
            type="text"
            placeholder="Enter last name"
            error={errors.lastName?.message}
            disabled={isSubmitting}
            {...register("lastName")}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-4">
          <FormInput
            label="Email Address"
            type="email"
            placeholder="Enter your email"
            error={errors.email?.message}
            disabled={isSubmitting}
            onKeyDown={(e) => {
              if (e.key === " ") {
                e.preventDefault();
              }
            }}
            {...register("email")}
          />

          <PhoneInput
            label="Contact Number"
            placeholder="10 digits no."
            error={errors.contactNumber?.message}
            disabled={isSubmitting}
            countryCode={countryCode}
            onCountryCodeChange={(code) => {
              setCountryCode(code);
              setValue("countryCode", code);
            }}
            onKeyDown={(e) => {
              // Allow control keys (backspace, delete, arrows, tab, etc.)
              if (
                e.key === "Backspace" ||
                e.key === "Delete" ||
                e.key === "ArrowLeft" ||
                e.key === "ArrowRight" ||
                e.key === "Tab" ||
                e.key === "Home" ||
                e.key === "End" ||
                e.ctrlKey ||
                e.metaKey
              ) {
                return;
              }
              // Block non-digit characters
              if (!/^\d$/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              // Strip any non-digit characters (handles paste, autofill, etc.)
              const input = e.currentTarget;
              input.value = input.value.replace(/\D/g, "");
            }}
            {...register("contactNumber")}
          />
        </div>

        <div>
          <label className="text-typography-800 mb-2 block text-sm font-semibold">
            Property Address
          </label>
          <Controller
            control={control}
            name="propertyAddress"
            render={({ field }) => (
              <div
                className={`flex h-10 items-center rounded border bg-white px-3 focus-within:ring-4 ${
                  errors.propertyAddress
                    ? "border-error-700 focus-within:border-error-700 focus-within:ring-error-200"
                    : "border-border-400 focus-within:border-primary-500 focus-within:ring-primary-200"
                }`}>
                <AddressSearchInput
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(val);
                    if (!val) setSelectedProperty(null);
                  }}
                  onPropertyChange={(property) => {
                    setSelectedProperty(property);
                    field.onChange(property?.address ?? "");
                  }}
                  placeholder="Enter your address…"
                  initiallySelected={!!selectedProperty}
                />
              </div>
            )}
          />
          {errors.propertyAddress && (
            <p className="text-error-600 mt-1 text-sm">{errors.propertyAddress.message}</p>
          )}
        </div>

        <div>
          <p className="text-typography-800 mb-3 text-sm font-semibold">
            I&apos;m interested in{" "}
            <span className="text-typography-500 font-normal">(select all that apply)</span>
          </p>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            {INTEREST_OPTIONS.map((option) => {
              const checked = interests.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() =>
                    setInterests((prev) =>
                      checked ? prev.filter((i) => i !== option) : [...prev, option],
                    )
                  }
                  className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                    checked
                      ? "border-primary-600 bg-primary-50 text-primary-700"
                      : "border-border-300 text-typography-700 hover:border-primary-400"
                  }`}>
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      checked ? "border-primary-600 bg-primary-600" : "border-border-400"
                    }`}>
                    {checked && (
                      <svg
                        className="h-2.5 w-2.5 text-white"
                        viewBox="0 0 10 8"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2">
                        <path d="M1 4l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  {option}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <FormInput
            label="Create Password"
            type="password"
            placeholder="Enter your password"
            error={
              errors.password?.message === "Password is required"
                ? errors.password.message
                : undefined
            }
            disabled={isSubmitting}
            onKeyDown={(e) => {
              if (e.key === " ") {
                e.preventDefault();
              }
            }}
            {...register("password")}
          />
          <PasswordValidationRules
            password={password}
            rules={passwordRules}
            touched={!!touchedFields.password}
          />
        </div>

        <FormInput
          label="Re-enter Password"
          type="password"
          placeholder="Confirm your password"
          error={errors.confirmPassword?.message}
          disabled={isSubmitting}
          onKeyDown={(e) => {
            if (e.key === " ") {
              e.preventDefault();
            }
          }}
          {...register("confirmPassword")}
        />
      </form>

      <AppButton
        type="submit"
        form="signup-form"
        className="bg-primary-700 h-10 w-full cursor-pointer rounded text-sm lg:h-12 lg:rounded-lg lg:text-lg"
        text="Create Account"
        isLoading={isSubmitting}
      />

      <div className="my-4 flex items-center lg:my-6">
        <div className="bg-border-200 h-px flex-1" />
        <span className="text-typography-500 px-3 text-xs font-medium lg:text-sm">
          or Signup with
        </span>
        <div className="bg-border-200 h-px flex-1" />
      </div>

      <SocialLogin />

      <p className="text-typography-700 mt-4 text-center text-xs lg:mt-6 lg:text-sm">
        <span className="font-medium">Have an account?</span>{" "}
        <Link href={ROUTES.AUTH.LOGIN} className="text-primary-700 font-semibold hover:underline">
          Login
        </Link>
      </p>
    </div>
  );
};
